import type { ControlPort, MeteringPort, Resource } from '@/features/resource/types'

/**
 * A control or metering port, flattened so one table can render either. `name` comes from the
 * control port's free-text `ports` or the metering port's `name`, and a port with no name is an
 * expected state rather than a gap.
 */
export type GroupedPort =
  | { kind: 'CONTROL'; id: string; name: string | null; errors: string | null; controlPort: ControlPort }
  | { kind: 'METERING'; id: string; name: string | null; errors: string | null; meteringPort: MeteringPort }

/**
 * The ports sitting on one controller. `equipmentNumber` is null for the catch-all group, which a
 * port can be in permanently — the identifier is nullable for a controller nobody has recorded, not
 * only while the backfill is outstanding.
 */
export type ControllerGroup = {
  equipmentNumber: number | null
  controllerName: string | null
  controllerImei: string | null
  ports: GroupedPort[]
}

/** A port this one relates to, and where it lives relative to this group. */
export type PortReference = {
  targetPortId: string
  targetPortName: string | null
  /**
   * True when the related port sits on a different controller, which the data model permits:
   * MeteredControlPortEntity joins a control port to a metering port with no controller constraint.
   * Grouping by controller therefore separates exactly these pairs, so the reference has to name
   * where it went.
   */
  isCrossController: boolean
  targetControllerName: string | null
  /**
   * The target's controller, when it has one. Carried alongside the name because the two absences
   * are different claims: no number means the controller is unidentifiable, while a number with no
   * name is the guaranteed shape of a controller contributing only control ports. Collapsing both
   * to "unknown" says we do not know which controller it is when we know exactly which one it is.
   */
  targetControllerEquipmentNumber: number | null
  /**
   * False when the port this relation names is not among the resource's own ports — an orphaned
   * relation, a metering port belonging to another resource, or one the BFF dropped.
   *
   * Reporting no reference at all would give the same answer as a port wired to nothing, and those
   * are opposite facts: one port is unwired, the other is wired to something we cannot show. When
   * this is false only `targetPortId` carries meaning, so every consumer branches on it first.
   */
  targetIsListed: boolean
}

function equipmentNumberOf(port: { controllerEquipmentNumber?: number | null }): number | null {
  return port.controllerEquipmentNumber ?? null
}

/**
 * A port's name, or null when it does not have one.
 *
 * `||`, not `??`: these are free text, so whitespace and the empty string are as good as absent, and
 * everything that renders a name falls back on null alone. Shared by the ports and by the references
 * pointing at them, since normalising one side leaves the other reading the raw field — a nameless
 * port showing its id in its own Name cell and an empty button in the cell of the row measuring it.
 */
function portNameOf(name: string | null | undefined): string | null {
  return name?.trim() || null
}

/**
 * Every metering port a control port is measured by.
 *
 * The list and the single `powerMeterID` are combined rather than one chosen over the other, so a
 * disagreement between the two fields cannot make a meter disappear. While the contract holds the
 * single id is already in the list and this adds nothing; the list leads, so its order is kept.
 *
 * One function, and every caller goes through it: reading either field directly is the bug this
 * exists to prevent.
 */
export function meterIdsOf(controlPort: Pick<ControlPort, 'powerMeterID' | 'powerMeterIDs'>): string[] {
  const single = controlPort.powerMeterID == null ? [] : [controlPort.powerMeterID]

  // Deduplicated: the same id twice is one relation, and nothing promises either field is distinct.
  return [...new Set([...(controlPort.powerMeterIDs ?? []), ...single])]
}

/**
 * One name per controller, for everything that has to name one.
 *
 * Only metering ports report `controllerName`, and a resource can carry several on one equipment
 * number. When they disagree the choice has to be made in one place — the header and the
 * cross-controller labels naming the same controller differently is worse than either name alone.
 * First one wins, which is the name the reader sees at the top.
 */
export function controllerNamesByEquipmentNumber(resource: Resource): Map<number, string> {
  const names = new Map<number, string>()

  for (const meteringPort of resource.meteringPorts) {
    const equipmentNumber = equipmentNumberOf(meteringPort)
    if (equipmentNumber === null || meteringPort.controllerName == null || names.has(equipmentNumber)) {
      continue
    }

    names.set(equipmentNumber, meteringPort.controllerName)
  }

  return names
}

/**
 * Groups a resource's ports by the controller they sit on.
 *
 * The controller's name and IMEI belong to the group rather than to any one port, which is what
 * lets a control port show a name it does not itself carry. Groups keep the order their first port
 * appears in, except the unknown-controller group, which is always last.
 */
export function buildControllerGroups(resource: Resource): ControllerGroup[] {
  const groups = new Map<number | 'unknown', ControllerGroup>()

  const groupFor = (port: { controllerEquipmentNumber?: number | null }): ControllerGroup => {
    const equipmentNumber = equipmentNumberOf(port)
    const key = equipmentNumber ?? 'unknown'
    const existing = groups.get(key)
    if (existing !== undefined) {
      return existing
    }

    const created: ControllerGroup = { equipmentNumber, controllerName: null, controllerImei: null, ports: [] }
    groups.set(key, created)

    return created
  }

  // Shared with the references, so the header cannot name a controller differently from them.
  const controllerNames = controllerNamesByEquipmentNumber(resource)

  const absorbControllerDetails = (group: ControllerGroup, port: { controllerImei?: string | null }) => {
    group.controllerName =
      group.equipmentNumber === null ? group.controllerName : (controllerNames.get(group.equipmentNumber) ?? null)
    group.controllerImei = group.controllerImei ?? port.controllerImei ?? null
  }

  // Control ports first, then metering, so rows do not follow whatever order the response used.
  for (const controlPort of resource.controlPorts) {
    const group = groupFor(controlPort)
    absorbControllerDetails(group, controlPort)
    group.ports.push({
      kind: 'CONTROL',
      id: controlPort.controlPortID,
      name: portNameOf(controlPort.ports),
      errors: controlPort.errors,
      controlPort,
    })
  }

  for (const meteringPort of resource.meteringPorts) {
    const group = groupFor(meteringPort)
    absorbControllerDetails(group, meteringPort)
    group.ports.push({
      kind: 'METERING',
      id: meteringPort.powerMeterID,
      name: portNameOf(meteringPort.name),
      errors: meteringPort.errors,
      meteringPort,
    })
  }

  const ordered = [...groups.values()]

  return [
    ...ordered.filter((group) => group.equipmentNumber !== null),
    ...ordered.filter((group) => group.equipmentNumber === null),
  ]
}

/**
 * What a port relates to across the resource. The relation is many-to-many in the data and in the
 * response, so neither direction is a single value.
 */
export function getPortReferences(port: GroupedPort, resource: Resource): PortReference[] {
  // Looked up by equipment number rather than read off the target: a control port carries no
  // controller name, and reading it there names every reverse reference "unknown".
  const controllerNames = controllerNamesByEquipmentNumber(resource)

  const describe = (
    targetPortId: string,
    targetPortName: string | null,
    target: { controllerEquipmentNumber?: number | null },
    source: { controllerEquipmentNumber?: number | null },
  ): PortReference => {
    const targetEquipmentNumber = equipmentNumberOf(target)

    return {
      targetPortId,
      targetPortName,
      /*
       * Two ports that both lack an equipment number count as the same controller, deliberately.
       * Calling it a crossing would assert two different controllers, which is exactly what is not
       * known, and a third "undetermined" treatment would give the ports we know least about the
       * most decorated edge. A known number against an unknown one is still a crossing: one of them
       * is identified, so the two cannot be the same equipment.
       */
      isCrossController: targetEquipmentNumber !== equipmentNumberOf(source),
      targetControllerName:
        targetEquipmentNumber === null ? null : (controllerNames.get(targetEquipmentNumber) ?? null),
      targetControllerEquipmentNumber: targetEquipmentNumber,
      targetIsListed: true,
    }
  }

  if (port.kind === 'CONTROL') {
    // Each id resolved on its own, since a list can hold both ids this resource lists and ids it
    // does not, and the unlisted ones are still relations.
    return meterIdsOf(port.controlPort).map((meteringPortId) => {
      const target = resource.meteringPorts.find((meteringPort) => meteringPort.powerMeterID === meteringPortId)

      // The id is all we have for it, so it is all the reference carries.
      if (target === undefined) {
        return {
          targetPortId: meteringPortId,
          targetPortName: null,
          isCrossController: false,
          targetControllerName: null,
          targetControllerEquipmentNumber: null,
          targetIsListed: false,
        }
      }

      return describe(target.powerMeterID, portNameOf(target.name), target, port.controlPort)
    })
  }

  return resource.controlPorts
    .filter((controlPort) => meterIdsOf(controlPort).includes(port.meteringPort.powerMeterID))
    .map((controlPort) =>
      describe(controlPort.controlPortID, portNameOf(controlPort.ports), controlPort, port.meteringPort),
    )
}
